/**
 * investor-profile.js
 * --------------------
 * UI logic for the Investor Profile modal.
 * This file wires the modal controls, handles selection events and posts
 * the selected investor profile to the backend.
 */

document.addEventListener('DOMContentLoaded', () => {
  const modalElement = document.getElementById('modal-investor-profile');

  if (!modalElement) {
    return;
  }

  const currentCountryCode = modalElement.dataset.currentCountryCode;
  const currentInvestorType = modalElement.dataset.currentInvestorType;
  
  if (currentCountryCode !== 'none' && currentInvestorType !== 'none') {
    document.querySelectorAll('#modal-investor-profile [data-bs-dismiss="modal"][data-btn-type="cancel"]').forEach((cancelButton) => {
      cancelButton.classList.remove('d-none');
    });
  } else {
    const modal = new bootstrap.Modal(modalElement);
    modal.show();
  }

  // Accept button — posts the selected profile to set a cookie and reloads on success.
  document.querySelectorAll('#modal-investor-profile [data-btn-type="accept"]').forEach((acceptButton) => {
    acceptButton.addEventListener('click', async () => {
      const savingIndicators = document.querySelectorAll('#modal-investor-profile [data-btn-type="saving"]');

      acceptButton.classList.toggle('d-none');
      savingIndicators.forEach((indicator) => {
        indicator.classList.toggle('d-none');
      });

      const countryCode = document.querySelector('#modal-investor-profile [data-country][selected]')?.getAttribute('data-country');
      const investorType = document.querySelector('#modal-investor-profile .investor-type-card[selected]')?.getAttribute('data-investor-type');

      try {
        const response = await fetch(`/investorprofile/${countryCode}/${investorType}`, {
          method: 'POST'
        });

        if (!response.ok) {
          savingIndicators.forEach((indicator) => {
            indicator.classList.toggle('d-none');
          });

          document.getElementById('alert-investor-profile-error')?.classList.toggle('d-none');
          return;
        }

        window.location.reload(true);
      } catch {
        // Intentionally swallow errors — leave the modal visible so the user may try again.
      }
    });
  });

  // Investor type card click — marks the card selected and emits a selection change event.
  const investorTypeCards = document.querySelectorAll('#modal-investor-profile .investor-type-card');
  investorTypeCards.forEach((investorTypeCard) => {
    investorTypeCard.addEventListener('click', () => {
      investorTypeCards.forEach((card) => {
        card.removeAttribute('selected');
      });

      investorTypeCard.setAttribute('selected', '');
      document.dispatchEvent(new Event('InvestorProfileSelectionChange'));
    });

    investorTypeCard.addEventListener('keyup', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        investorTypeCard.click();
      }
    });
  });

  // Country button click — marks the button selected, scrolls to investor type
  // selection, and emits a selection change event.
  const countryButtons = document.querySelectorAll('#modal-investor-profile [data-country]');
  countryButtons.forEach((countryButton) => {
    countryButton.addEventListener('click', () => {
      countryButtons.forEach((button) => {
        button.removeAttribute('selected');
      });

      countryButton.setAttribute('selected', '');

      document.querySelector('#modal-investor-profile .investor-type-card')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      document.dispatchEvent(new Event('InvestorProfileSelectionChange'));
    });
  });

  // Modal reset — restores the modal to its default state when hidden.
  modalElement.addEventListener('hidden.bs.modal', () => {
    document.querySelectorAll('#modal-investor-profile [data-country][selected]').forEach((button) => {
      button.removeAttribute('selected');
    });

    document.querySelectorAll('#modal-investor-profile .investor-type-card[selected]').forEach((button) => {
      button.removeAttribute('selected');
    });

    document.getElementById('alert-investor-profile-error')?.classList.add('d-none');

    document.querySelectorAll('#modal-investor-profile [data-btn-type="accept"]').forEach((acceptButton) => {
      acceptButton.classList.add('d-none');
      acceptButton.disabled = true;
    });

    document.querySelectorAll('#modal-investor-profile [data-btn-type="saving"]').forEach((indicator) => {
      indicator.classList.add('d-none');
    });

    document.querySelectorAll('#modal-investor-profile button[data-bs-slide="prev"]').forEach((prevButton) => {
      prevButton.classList.add('d-none');
      prevButton.click();
    });

    document.querySelectorAll('#modal-investor-profile button[data-bs-slide="next"]').forEach((nextButton) => {
      nextButton.classList.remove('d-none');
      nextButton.disabled = true;
    });
  });

  // Prev/Next button handlers for the carousel navigation.
  const preview = document.querySelector('#modal-header-country-preview');
  const prevButtons = document.querySelectorAll('#modal-investor-profile button[data-bs-slide="prev"]');
  const nextButtons = document.querySelectorAll('#modal-investor-profile button[data-bs-slide="next"]');
  const acceptButtons = document.querySelectorAll('#modal-investor-profile [data-btn-type="accept"]');
  const tandcHeading = document.querySelector('#modal-header-tandc-heading');
  const terms = document.querySelector('#modal-investor-terms .inner-terms');

  nextButtons.forEach((btnNext) => {
    btnNext.addEventListener('click', () => {
      const selectedCountry = document.querySelector('#investor-profile-carousel button[data-country][selected]');
      const selectedInvestorType = document.querySelector('#investor-profile-carousel .investor-type-card[data-investor-type][selected]');

      if (!selectedCountry || !selectedInvestorType) {
        return;
      }

      if (preview) {
        preview.innerHTML = `<span class="fi fi-${selectedCountry.dataset.country.toLowerCase()} me-2"></span>${selectedInvestorType.dataset.investorType}`;
        preview.parentElement.classList.remove('d-none');
        preview.classList.add('show');
      }

      if (tandcHeading) {
        tandcHeading.classList.remove('d-none');
        tandcHeading.classList.add('show');
      }

      if (terms) {
        terms.classList.remove('d-none');
      }

      nextButtons.forEach((btn) => {
        btn.classList.add('d-none');
      });

      prevButtons.forEach((btnPrev) => {
        btnPrev.classList.add('show');
        btnPrev.classList.remove('d-none');
        btnPrev.disabled = false;
      });

      acceptButtons.forEach((btnAccept) => {
        btnAccept.classList.remove('d-none');
        btnAccept.disabled = false;
      });
    });
  });

  prevButtons.forEach((btnPrev) => {
    btnPrev.addEventListener('click', () => {
      preview?.classList.toggle('show');
      preview?.parentElement.classList.toggle('d-none');

      if (tandcHeading) {
        tandcHeading.classList.toggle('show');
        tandcHeading.classList.toggle('d-none');
      }

      prevButtons.forEach((btn) => {
        btn.classList.toggle('show');
        btn.disabled = true;
      });

      if (terms) {
        terms.classList.toggle('d-none');
      }

      nextButtons.forEach((btn) => {
        btn.classList.toggle('d-none');
      });

      acceptButtons.forEach((btnAccept) => {
        btnAccept.classList.add('d-none');
        btnAccept.disabled = true;
      });
    });
  });
});

// Handles the InvestorProfileSelectionChange event to enable/disable Next buttons
// and populate the terms text based on the current selection.
document.addEventListener('InvestorProfileSelectionChange', async () => {
  const selectedCountryButton = document.querySelector('#modal-investor-profile [data-country][selected]');
  const selectedTypeButton = document.querySelector('#modal-investor-profile .investor-type-card[selected]');
  const shouldEnable = Boolean(selectedCountryButton && selectedTypeButton);

  document.querySelectorAll('#modal-investor-profile [data-bs-slide="next"]').forEach((nextButton) => {
    nextButton.disabled = !shouldEnable;
  });

  if (shouldEnable) {
    await addCorrespondingTermsText(selectedCountryButton.dataset.country, selectedTypeButton.dataset.investorType);
  }

  async function addCorrespondingTermsText(countryCode, investorType) {
    const termsTextElement = document.querySelector('#modal-investor-terms .inner-terms');
    if (!termsTextElement) {
      return;
    }

    termsTextElement.innerHTML = '<div class="d-flex justify-content-center py-4"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>';

    try {
      const response = await fetch(`/investorprofile/${countryCode}/${investorType}/terms`);
      
      if (response.ok) {
        termsTextElement.innerHTML = await response.text();
      } else {
        termsTextElement.innerHTML = '';
      }
    } catch {
      termsTextElement.innerHTML = '';
    }
  }
});
